import { z } from "zod";
import manifestJson from "./manifest.generated.json" with { type: "json" };

export const ASSET_KINDS = [
  "building",
  "ground",
  "decor",
  "avatar",
  "cosmetic",
  "item",
  "prop",
  "ui",
  "reward",
  "logo",
  "icon",
  "portrait",
] as const;

export const assetEntrySchema = z
  .object({
    id: z.string().regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/),
    kind: z.enum(ASSET_KINDS),
    source: z.literal("gpt-image-bridge"),
    /** Source sheet in art/source and the cell it was cut from. */
    sheet: z.string(),
    file: z.string().regex(/^\/assets\/v\d+\/.+\.(webp|png)$/),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    anchor: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }),
    artStyleVersion: z.string(),
    status: z.enum(["approved", "pending_review", "rejected"]),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    tintable: z.boolean().optional(),
  })
  .strict();
export type AssetEntry = z.infer<typeof assetEntrySchema>;

export const assetManifestSchema = z.array(assetEntrySchema);

export const ASSET_MANIFEST: readonly AssetEntry[] = assetManifestSchema.parse(manifestJson);

export const ASSET_BY_ID: ReadonlyMap<string, AssetEntry> = new Map(
  ASSET_MANIFEST.map((entry) => [entry.id, entry]),
);

/**
 * Whether a build may show this asset. Production only ships approved art;
 * development and preview builds also show art that is waiting for review.
 */
export function isAssetUsable(entry: AssetEntry | undefined, allowPending: boolean): entry is AssetEntry {
  if (!entry) return false;
  return entry.status === "approved" || (allowPending && entry.status === "pending_review");
}
